import type React from "react"
import { createInertiaApp } from "@inertiajs/react"
import { createRoot } from "react-dom/client"
import { ConfirmDialogProvider } from "@/components/confirm-dialog"
import { ErrorBoundary } from "@/components/error-boundary"
import { AppLayout } from "@/layout/AppLayout"
import { MarketingLayout } from "@/layout/MarketingLayout"
import { startPostHog, type PostHogSettings } from "@/lib/posthog"

import "@/entrypoints/application.css"
import "@fontsource-variable/inter/index.css"
import "@fontsource/jetbrains-mono/400.css"
import "@fontsource/jetbrains-mono/500.css"

type InertiaPage = React.ComponentType<Record<string, unknown>> & {
  layout?: (page: React.ReactNode) => React.ReactNode
}

const pages = import.meta.glob<{ default: InertiaPage }>(
  "../pages/**/*.tsx",
)

// One stable function reference per layout, shared across every page, so
// Inertia recognizes consecutive pages as using "the same" layout and keeps
// it mounted (persistent layouts) instead of remounting it on every visit.
// Pages under pages/public/** get MarketingLayout; everything else gets
// AppLayout. A page can opt out by setting its own `.layout` before export.
const withAppLayout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
const withMarketingLayout = (page: React.ReactNode) => <MarketingLayout>{page}</MarketingLayout>

// Each page is a separate hash-named chunk fetched over the network per visit.
// A dropped mobile request, or a deploy that rotated the hashes under an open
// tab, makes the import reject. That rejection escapes React, so the app-root
// ErrorBoundary can never catch it, and the driver is stuck on a dead page.
// Retry once for a transient failure; if it still fails, hard-reload so the
// browser re-reads the current manifest and requests the right chunk.
async function importChunk<T>(importer: () => Promise<T>): Promise<T> {
  try {
    return await importer()
  } catch {
    try {
      return await importer()
    } catch {
      window.location.reload()
      // The reload navigates away. Never resolve, so the failed import does
      // not surface before the fresh document loads.
      return new Promise<T>(() => {})
    }
  }
}

createInertiaApp({
  resolve: async (name) => {
    const importer = pages[`../pages/${name}.tsx`]
    if (!importer) {
      throw new Error(`Inertia page not found: ${name}`)
    }
    const mod = await importChunk(importer)
    const page = mod.default
    if (page.layout === undefined) {
      page.layout = name.startsWith("public/") ? withMarketingLayout : withAppLayout
    }
    return page
  },
  setup({ el, App, props }) {
    // Before the first render, so the initial pageview and the identify land
    // in the right order. Nil unless POSTHOG_TOKEN is set, which is how
    // development and test stay out of the project — see
    // ApplicationController's `posthog` shared prop.
    const posthogSettings = props.initialPage.props.posthog as PostHogSettings | null
    if (posthogSettings) startPostHog(posthogSettings, props.initialPage.props)

    createRoot(el).render(
      <ErrorBoundary>
        <ConfirmDialogProvider>
          <App {...props} />
        </ConfirmDialogProvider>
      </ErrorBoundary>,
    )
  },
  progress: { color: "var(--color-primary)" },
})
